import {
  useCallback,
  useMemo,
  createContext,
  useContext,
  useState,
  useLayoutEffect,
} from 'react'
import {
  RaceQuery,
  RaceUpdatedSubscription,
  PriceUpdatesSubscription,
  ResultsSubscription,
  useRaceQuery,
  useRaceUpdatedSubscription,
  usePriceUpdatesSubscription,
  useResultsSubscription,
} from '../generated/graphql'
import type {
  ApolloError,
  SubscriptionResult,
  WatchQueryFetchPolicy,
} from '@apollo/client'

interface RaceContextI {
  id: null | string
  queryParams: Parameters<typeof useRaceQuery>[0]
  subscriptionParams: Parameters<typeof useRaceUpdatedSubscription>[0]

  /** @description merged race and subscription data */
  race: null | Omit<NonNullable<RaceQuery['race']>, '__typename'>
  previousRaceData: ReturnType<typeof useRaceQuery>['previousData']

  loading: boolean
  error?: ApolloError
  retry: () => void
}

const CurrentRaceContext = createContext<null | RaceContextI>(null)

export const CurrentRaceProvider = CurrentRaceContext.Provider

export const useCurrentRace = () => useContext(CurrentRaceContext)

export const useRaceFetcher = (
  id: string | null,
  skip = false,
  cachePolicy: WatchQueryFetchPolicy = 'cache-and-network'
): RaceContextI => {
  const [subscriptionData, setSubscriptionData] = useState<
    null | SubscriptionResult<RaceUpdatedSubscription>['data']
  >(null)
  const [priceSetsFromSubscription, setPriceSetsFromSubscription] = useState<
    PriceUpdatesSubscription['priceUpdates'][]
  >([])
  const [resultSetsFromSubscription, setResultSetsFromSubscription] = useState<
    ResultsSubscription['results'][]
  >([])

  const raceQueryParams = useMemo(
    (): Parameters<typeof useRaceQuery>[0] => ({
      fetchPolicy: cachePolicy,
      pollInterval: 120000,
      variables: {
        id,
        silksSize: 64,
      },
      skip: !id || skip,
    }),
    [id, skip, cachePolicy]
  )

  const raceSubscriptionUpdater = useCallback(
    ({
      subscriptionData,
    }: {
      subscriptionData: SubscriptionResult<RaceUpdatedSubscription>
    }) => setSubscriptionData(subscriptionData?.data),
    [setSubscriptionData]
  )

  const priceUpdatesUpdater = useCallback(
    ({
      subscriptionData,
    }: {
      subscriptionData: SubscriptionResult<PriceUpdatesSubscription>
    }) => {
      const set = subscriptionData?.data?.priceUpdates
      if (!set) return
      setPriceSetsFromSubscription((prev) => {
        const key = `${set.source}-${set.type}`
        const next = prev.filter(
          (s) => `${s.source}-${s.type}` !== key
        )
        return [...next, set]
      })
    },
    []
  )

  const resultsUpdater = useCallback(
    ({
      subscriptionData,
    }: {
      subscriptionData: SubscriptionResult<ResultsSubscription>
    }) => {
      const resultSet = subscriptionData?.data?.results
      if (!resultSet) return
      setResultSetsFromSubscription((prev) => {
        const next = prev.filter((r) => r.source !== resultSet.source)
        return [...next, resultSet]
      })
    },
    []
  )

  const raceSubscriptionParams = useMemo(
    (): Parameters<typeof useRaceUpdatedSubscription>[0] => ({
      fetchPolicy: 'network-only' as const,
      variables: {
        raceId: id,
      },
      skip: !id || skip,
      onSubscriptionData: raceSubscriptionUpdater,
    }),
    [id, skip, raceSubscriptionUpdater]
  )

  const priceUpdatesSubscriptionParams = useMemo(
    (): Parameters<typeof usePriceUpdatesSubscription>[0] => ({
      variables: { raceId: id },
      skip: !id || skip,
      onSubscriptionData: priceUpdatesUpdater,
    }),
    [id, skip, priceUpdatesUpdater]
  )

  const resultsSubscriptionParams = useMemo(
    (): Parameters<typeof useResultsSubscription>[0] => ({
      variables: { raceId: id },
      skip: !id || skip,
      onSubscriptionData: resultsUpdater,
    }),
    [id, skip, resultsUpdater]
  )

  const raceFetch = useRaceQuery(raceQueryParams)

  useRaceUpdatedSubscription(raceSubscriptionParams)
  usePriceUpdatesSubscription(priceUpdatesSubscriptionParams)
  useResultsSubscription(resultsSubscriptionParams)

  const retry = useCallback(() => void raceFetch.refetch(), [raceFetch])

  const race = raceFetch?.data?.race

  useLayoutEffect(() => {
    setSubscriptionData(null)
    setPriceSetsFromSubscription([])
    setResultSetsFromSubscription([])
  }, [raceQueryParams?.variables?.id, id, skip])

  const raceMerged = useMemo(() => {
    if (!race) return null

    const raceIdMatch = race.id === subscriptionData?.raceUpdated?.id
    const mergedCompetitors: typeof race.competitors = race.competitors?.map(
      (competitorOriginal) => {
        const updatedCompetitor = raceIdMatch
          ? subscriptionData?.raceUpdated?.competitors?.find(
              (c) => c.tabNo === competitorOriginal.tabNo
            )
          : undefined
        const subscriptionSourceTypes = new Set(
          priceSetsFromSubscription.map((ps) => `${ps.source}-${ps.type}`)
        )
        const keptPrices = (competitorOriginal.prices ?? []).filter(
          (p) => !subscriptionSourceTypes.has(`${p.source}-${p.type}`)
        )
        const newPricesFromSub = priceSetsFromSubscription.flatMap((ps) =>
          (ps.prices ?? []).filter((pr) => pr.tabNo === competitorOriginal.tabNo)
        )
        const prices = [...keptPrices, ...newPricesFromSub]

        return {
          ...competitorOriginal,
          scratched:
            updatedCompetitor?.scratched ?? competitorOriginal.scratched,
          prices: prices.length ? prices : competitorOriginal.prices,
          __typename: undefined,
        }
      }
    )

    const mergedPriceSets =
      priceSetsFromSubscription.length > 0
        ? (() => {
            const subKeys = new Set(
              priceSetsFromSubscription.map((ps) => `${ps.source}-${ps.type}`)
            )
            const fromQuery = (race.priceSets ?? []).filter(
              (ps) => !subKeys.has(`${ps.source}-${ps.type}`)
            )
            return [...fromQuery, ...priceSetsFromSubscription]
          })()
        : race.priceSets

    const mergedResults =
      resultSetsFromSubscription.length > 0
        ? (() => {
            const subSources = new Set(
              resultSetsFromSubscription.map((r) => r.source)
            )
            const fromQuery = (race.results ?? []).filter(
              (r) => !subSources.has(r.source)
            )
            return [...fromQuery, ...resultSetsFromSubscription]
          })()
        : race.results

    return {
      ...race,
      ...(raceIdMatch && subscriptionData?.raceUpdated
        ? {
            status: subscriptionData.raceUpdated.status,
            startTime: subscriptionData.raceUpdated.startTime,
            surfaceType: subscriptionData.raceUpdated.surfaceType,
          }
        : {}),
      competitors: mergedCompetitors,
      priceSets: mergedPriceSets,
      results: mergedResults,
      __typename: undefined,
    }
  }, [
    race,
    subscriptionData?.raceUpdated,
    priceSetsFromSubscription,
    resultSetsFromSubscription,
  ])

  return {
    id,
    retry,
    queryParams: raceQueryParams,
    subscriptionParams: raceSubscriptionParams,
    race: raceMerged,
    loading: raceFetch?.loading,
    previousRaceData: raceFetch?.previousData,
  }
}

export function useRace(
  id: string | null = null,
  skip = false,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-first'
) {
  const currentRace = useCurrentRace()

  const isCurrentRace = currentRace?.id === id

  const fetchedRace = useRaceFetcher(
    id,
    !id || isCurrentRace || skip,
    fetchPolicy
  )

  return isCurrentRace ? currentRace : fetchedRace
}

